import React, { Suspense, useRef, useEffect, useLayoutEffect, useState, useMemo, useCallback } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { Home } from 'lucide-react';
import { collectMorphNames, mergeExpressions } from '../lib/expressions';
import { applyLooks, collectParts, computeSwaps } from '../lib/looks';
import { fileUrl } from '../lib/api';
import { applyShading, eachMaterial } from '../lib/shading';
import { applyOutline, outlineUniforms } from '../lib/outline';
import { rigFor, lightPosition, AIMED } from '../lib/lights';

function renderGeometry(primitive) {
  switch (primitive) {
    case 'torusKnot': return <torusKnotGeometry args={[0.9, 0.28, 140, 20]} />;
    case 'icosahedron': return <icosahedronGeometry args={[1.1, 1]} />;
    case 'octahedron': return <octahedronGeometry args={[1.2, 0]} />;
    case 'dodecahedron': return <dodecahedronGeometry args={[1.15, 0]} />;
    case 'box': return <boxGeometry args={[1.4, 1.4, 1.4]} />;
    default: return <sphereGeometry args={[1.2, 48, 48]} />;
  }
}

function PrimitiveShape({ primitive, color }) {
  const ref = useRef();
  useFrame((_, delta) => {
    if (ref.current) {
      ref.current.rotation.y += delta * 0.25;
      ref.current.rotation.x += delta * 0.08;
    }
  });
  return (
    <group ref={ref}>
      <mesh>
        {renderGeometry(primitive)}
        <meshStandardMaterial
          color={color || '#f5a623'}
          metalness={0.35}
          roughness={0.4}
          emissive={color || '#f5a623'}
          emissiveIntensity={0.1}
        />
      </mesh>
      <mesh scale={1.01}>
        {renderGeometry(primitive)}
        <meshBasicMaterial color="#1a0e05" wireframe transparent opacity={0.22} />
      </mesh>
    </group>
  );
}

/**
 * GltfModel + AutoFitCamera replaces drei's <Bounds>/<Center> combo.
 *
 * Why: Bounds runs *after* the first paint, so the model briefly renders
 * at its raw (un-centered, un-fit) position for one frame and then snaps.
 * We instead use useLayoutEffect to compute the bounding box and write the
 * model offset + camera position SYNCHRONOUSLY before React paints, so the
 * very first frame is already correct.
 */
const texCache = new Map();
function loadTex(url) {
  if (!texCache.has(url)) {
    texCache.set(url, new Promise((resolve) => {
      new THREE.TextureLoader().load(url, (tex) => {
        tex.flipY = false; // glTF UV convention
        tex.colorSpace = THREE.SRGBColorSpace;
        resolve(tex);
      }, undefined, () => resolve(null));
    }));
  }
  return texCache.get(url);
}

const WHITE = new THREE.Color('#ffffff');

// Final colour/map per material: the file's own, then Admin-added textures, then active Look swaps (later wins).
function useMaterialPaint(scene, textures, swaps, shading, invalidate) {
  const key = JSON.stringify([(textures || []).map((t) => [t.material, t.url]), swaps || []]);
  useEffect(() => {
    const [extra, sw] = JSON.parse(key);
    let live = true;
    const swapBy = {};
    sw.forEach((x) => { swapBy[x.material] = { ...swapBy[x.material], ...(x.color ? { color: x.color } : {}), ...(x.texture ? { url: fileUrl(x.texture) } : {}) }; });
    const paint = (loaded) => {
      const extraBy = Object.fromEntries(extra.map(([m, u]) => [m, loaded[u]]));
      eachMaterial(scene, (mat) => {
        const own = mat.userData.__orig?.userData.__base;
        if (!own && !mat.userData.__base) mat.userData.__base = { map: mat.map, color: mat.color.clone() };
        const base = own || mat.userData.__base;
        const x = swapBy[mat.name];
        const tex = (x?.url && loaded[x.url]) || extraBy[mat.name] || null;
        const map = tex || base.map;
        if (mat.map !== map) { mat.map = map; mat.needsUpdate = true; }
        mat.color.set(x?.color || (tex ? WHITE : base.color));
      });
      invalidate();
    };
    const urls = [...extra.map((e) => e[1]), ...Object.values(swapBy).map((x) => x.url).filter(Boolean)];
    const loaded = {};
    paint(loaded);
    Promise.all(urls.map((u) => loadTex(u).then((t) => { loaded[u] = t; }))).then(() => { if (live) paint(loaded); });
    return () => { live = false; };
  }, [scene, key, shading, invalidate]);
}

function useOutline(scene, outline, shading, invalidate) {
  const { gl } = useThree();
  const on = shading === 'toon' && !!outline?.on;
  const width = outline?.width ?? 2;
  const color = outline?.color || '#000000';
  useLayoutEffect(() => { applyOutline(scene, { on, color }); invalidate(); }, [scene, on, color, invalidate]);
  // Card and lightbox can share this scene, so set the pixel size from this canvas before each render.
  useFrame(() => {
    if (!on) return;
    const u = outlineUniforms(scene);
    gl.getDrawingBufferSize(u.outlineRes.value);
    u.outlineWidth.value = width * gl.getPixelRatio();
  });
}

function GltfModel({ url, controlsRef, expression, onMorphNames, looks, activeLooks, onParts, textures, shading, outline, onFit }) {
  const { scene } = useGLTF(url);
  const groupRef = useRef();
  const { camera, size, invalidate } = useThree();
  const activeKey = (activeLooks || []).join('|');
  useLayoutEffect(() => { applyShading(scene, shading); invalidate(); }, [scene, shading, invalidate]);
  useOutline(scene, outline, shading, invalidate);
  const swaps = useMemo(() => computeSwaps(looks, activeKey ? activeKey.split('|') : []), [looks, activeKey]);
  useMaterialPaint(scene, textures, swaps, shading, invalidate);

  // Part visibility comes from the default look + active toggles (scene is shared, so always reapply).
  useLayoutEffect(() => {
    applyLooks(scene, looks, activeKey ? activeKey.split('|') : []);
    invalidate();
  }, [scene, looks, activeKey, invalidate]);

  useEffect(() => { onParts?.(collectParts(scene)); }, [scene, onParts]);
  const morphMeshes = useMemo(() => {
    const list = [];
    scene.traverse((o) => { if (o.isMesh && !o.userData.__isOutline && o.morphTargetDictionary && o.morphTargetInfluences) list.push(o); });
    return list;
  }, [scene]);
  const targetRef = useRef(expression);
  targetRef.current = expression;

  // The GLTF scene is cached and shared, so start every mount from the neutral face.
  useLayoutEffect(() => {
    morphMeshes.forEach((m) => m.morphTargetInfluences.fill(0));
  }, [morphMeshes]);

  useEffect(() => { onMorphNames?.(collectMorphNames(scene)); }, [scene, onMorphNames]);

  // Ease every influence toward its target (~0.5s), so faces blend instead of snapping.
  useFrame((_, delta) => {
    if (!morphMeshes.length) return;
    const k = 1 - Math.exp(-Math.min(delta, 0.1) * 9);
    for (const m of morphMeshes) {
      const idx = targetRef.current != null ? m.morphTargetDictionary[targetRef.current] : undefined;
      const inf = m.morphTargetInfluences;
      for (let i = 0; i < inf.length; i += 1) inf[i] += ((i === idx ? 1 : 0) - inf[i]) * k;
    }
  });

  useLayoutEffect(() => {
    if (!groupRef.current) return;
    // Reset transform so re-mounts start from a known state.
    groupRef.current.position.set(0, 0, 0);

    const box = new THREE.Box3().setFromObject(scene);
    if (box.isEmpty()) return;

    const center = new THREE.Vector3();
    const sizeVec = new THREE.Vector3();
    box.getCenter(center);
    box.getSize(sizeVec);

    // Translate the model so its bbox center sits at the world origin.
    groupRef.current.position.set(-center.x, -center.y, -center.z);

    // Distance the camera so the whole bounding sphere fits in view.
    const radius = Math.max(sizeVec.x, sizeVec.y, sizeVec.z) * 0.5 || 1;
    onFit?.(radius);
    const fov = (camera.fov * Math.PI) / 180;
    const aspect = size.width / Math.max(size.height, 1);
    const distV = radius / Math.tan(fov / 2);
    const distH = radius / Math.tan(fov / 2) / Math.max(aspect, 0.0001);
    const distance = Math.max(distV, distH) * 1.6;

    // Place camera on a 3/4 angle so the model has nice depth.
    const dir = new THREE.Vector3(0.7, 0.45, 1).normalize();
    camera.position.copy(dir.multiplyScalar(distance));
    camera.lookAt(0, 0, 0);
    camera.near = Math.max(distance / 1000, 0.01);
    camera.far = distance * 100;
    camera.updateProjectionMatrix();

    if (controlsRef?.current) {
      controlsRef.current.target.set(0, 0, 0);
      controlsRef.current.update();
      // Mark this as the new "home" so OrbitControls.reset() returns here.
      controlsRef.current.saveState();
    }
  }, [scene, camera, size.width, size.height, controlsRef, onFit]);

  return <primitive ref={groupRef} object={scene} />;
}

// Admin-only glowing markers where each aimed light sits (suns are drawn just outside the model).
function LightMarkers({ item, radius, highlight }) {
  const rig = rigFor(item).filter((l) => l.on !== false && AIMED.has(l.type));
  return rig.map((l) => {
    const r = radius * (l.type === 'directional' ? 1.5 : l.dist);
    const pos = lightPosition(l.az, l.el, r);
    const size = radius * (l.id === highlight ? 0.09 : 0.055);
    return (
      <group key={l.id} position={pos}>
        <mesh raycast={() => null}>
          <sphereGeometry args={[size, 16, 12]} />
          <meshBasicMaterial color={l.color} toneMapped={false} />
        </mesh>
        <mesh raycast={() => null}>
          <sphereGeometry args={[size * 1.9, 16, 12]} />
          <meshBasicMaterial color={l.id === highlight ? '#ffc968' : l.color} transparent opacity={0.25} depthWrite={false} toneMapped={false} />
        </mesh>
      </group>
    );
  });
}

function Lights({ item, radius }) {
  const { gl, invalidate } = useThree();
  const brightness = item?.brightness ?? 1;
  // Exposure (not light strength) so brightness also works on matcap and unlit materials.
  useEffect(() => { gl.toneMappingExposure = brightness; invalidate(); }, [gl, brightness, invalidate]);
  const rig = rigFor(item).filter((l) => l.on !== false);
  useEffect(() => { invalidate(); });
  return rig.map((l) => {
    const props = { color: l.color, intensity: l.intensity };
    if (l.type === 'ambient') return <ambientLight key={l.id} {...props} />;
    if (l.type === 'hemisphere') return <hemisphereLight key={l.id} {...props} groundColor={l.ground} />;
    const pos = lightPosition(l.az, l.el, radius * (l.type === 'directional' ? 4 : l.dist));
    if (l.type === 'point') return <pointLight key={l.id} {...props} position={pos} decay={0} />;
    if (l.type === 'spot') {
      return <spotLight key={l.id} {...props} position={pos} decay={0} angle={(l.angle * Math.PI) / 180} penumbra={l.penumbra} />;
    }
    return <directionalLight key={l.id} {...props} position={pos} />;
  });
}

function ViewportRefit({ hasGltf }) {
  const { camera, gl, size } = useThree();
  useEffect(() => {
    const parent = gl.domElement.parentElement;
    if (parent) {
      const rect = parent.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        gl.setSize(rect.width, rect.height, false);
        if (camera.isPerspectiveCamera) {
          camera.aspect = rect.width / rect.height;
          camera.updateProjectionMatrix();
        }
      }
    }
    // Don't override camera target for GLTF models — <Bounds> manages it.
    if (!hasGltf) camera.lookAt(0, 0, 0);
  }, [camera, gl, size.width, size.height, hasGltf]);
  return null;
}

const CLICK_DRAG_THRESHOLD_PX = 6;

export default function ModelViewer({ item, expanded = false, fillContainer = false, onExpand, expression = null, onMorphNames, looks, activeLooks, onParts, showLights = false, highlightLight = null }) {
  const heightStyle = fillContainer ? '100%' : (expanded ? 460 : 280);
  const hasGltf = Boolean(item?.modelUrl);
  const controlsRef = useRef(null);
  const wrapperRef = useRef(null);
  const [resetTick, setResetTick] = useState(0);
  const [ready, setReady] = useState(false);
  const [morphNames, setMorphNames] = useState([]);
  const [radius, setRadius] = useState(1);
  const handleMorphNames = useCallback((names) => {
    setMorphNames((prev) => (prev.join('|') === names.join('|') ? prev : names));
    onMorphNames?.(names);
  }, [onMorphNames]);
  const hasFaces = !expanded && mergeExpressions(item?.expressions, morphNames).some((e) => !e.hidden);
  const effectiveLooks = looks === undefined ? item?.looks : looks;
  const hasLooks = !expanded && (item?.looks?.toggles?.length || 0) > 0;

  // Track pointer-down position so we can tell a click apart from a drag.
  const downRef = useRef({ x: 0, y: 0, time: 0, dragged: false });

  useEffect(() => {
    setReady(false);
    const t = setTimeout(() => setReady(true), 140);
    return () => clearTimeout(t);
  }, [item?.id, item?.modelUrl, expanded, resetTick]);

  // Re-fit only when the wrapper changes size by a meaningful amount AFTER
  // the initial mount (e.g. user resizes the window). We skip the first
  // observation so we don't re-trigger Bounds while the lightbox is opening.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let last = null;
    let raf = 0;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0].contentRect;
      if (last === null) { last = { w: cr.width, h: cr.height }; return; }
      if (Math.abs(cr.width - last.w) < 8 && Math.abs(cr.height - last.h) < 8) return;
      last = { w: cr.width, h: cr.height };
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setResetTick((t) => t + 1));
    });
    ro.observe(el);
    return () => { ro.disconnect(); cancelAnimationFrame(raf); };
  }, []);

  const recenter = (e) => {
    e?.stopPropagation();
    if (controlsRef.current) controlsRef.current.reset();
    setResetTick((t) => t + 1);
  };

  const handlePointerDown = (e) => {
    downRef.current = { x: e.clientX, y: e.clientY, time: Date.now(), dragged: false };
  };
  const handlePointerMove = (e) => {
    const d = downRef.current;
    if (!d.time) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (dx * dx + dy * dy > CLICK_DRAG_THRESHOLD_PX * CLICK_DRAG_THRESHOLD_PX) {
      d.dragged = true;
    }
  };
  const handlePointerUp = (e) => {
    const d = downRef.current;
    const dt = Date.now() - d.time;
    downRef.current = { x: 0, y: 0, time: 0, dragged: false };
    if (expanded || !onExpand) return;
    // Click (not drag, not long-press): open lightbox.
    if (!d.dragged && dt < 400) {
      onExpand(item, e);
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="relative w-full"
      style={{ height: heightStyle, cursor: expanded ? 'grab' : 'pointer' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <Canvas
        key={expanded ? 'expanded' : 'card'}
        camera={{ position: [2.8, 1.6, 3.2], fov: 42 }}
        dpr={[1, 2]}
        gl={{ toneMapping: THREE.NeutralToneMapping }}
        style={{
          background: 'radial-gradient(ellipse at center, #2a1608 0%, #0f0704 75%)',
          width: '100%',
          height: '100%',
          display: 'block',
          opacity: ready ? 1 : 0,
          transition: 'opacity 180ms ease-out'
        }}
      >
        <Lights item={item} radius={radius} />
        {showLights && <LightMarkers item={item} radius={radius} highlight={highlightLight} />}
        <Suspense fallback={null}>
          {hasGltf ? (
            <GltfModel key={resetTick} url={item.modelUrl} controlsRef={controlsRef} expression={expression} onMorphNames={handleMorphNames}
                       looks={effectiveLooks} activeLooks={activeLooks} onParts={onParts} textures={item.textures} shading={item.shading} outline={item.outline} onFit={setRadius} />
          ) : (
            <PrimitiveShape primitive={item?.primitive} color={item?.color} />
          )}
        </Suspense>
        <ViewportRefit hasGltf={hasGltf} />
        <OrbitControls
          ref={controlsRef}
          enablePan={expanded}
          enableZoom
          minDistance={0.5}
          maxDistance={20}
          target={[0, 0, 0]}
          autoRotate={!expanded}
          autoRotateSpeed={0.6}
        />
      </Canvas>

      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0 2px, rgba(0,0,0,0.1) 3px 4px)',
        mixBlendMode: 'multiply'
      }} />

      <div className="absolute top-2 left-2 font-osd text-xs text-[#f5a623] bg-[#1a0e05]/80 px-2 py-1 rounded border border-[#f5a623]/50 pointer-events-none">
        3D // {item?.id?.toUpperCase()}
      </div>

      {(hasFaces || hasLooks) && (
        <div className="absolute bottom-2 left-2 flex gap-1.5 pointer-events-none">
          {hasFaces && <Badge testId="model-faces-badge">FACES</Badge>}
          {hasLooks && <Badge testId="model-looks-badge">LOOKS</Badge>}
        </div>
      )}

      {/* Home/recenter button — only visible in expanded view */}
      {expanded && (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={recenter}
          title="Recenter model"
          aria-label="Recenter model"
          data-testid="model-recenter-btn"
          className="absolute bottom-2 left-2 w-8 h-8 font-osd text-xs text-[#f5a623] bg-[#1a0e05]/85 hover:bg-[#f5a623] hover:text-[#1a0e05] rounded border border-[#f5a623]/60 transition-colors flex items-center justify-center"
        >
          <Home className="w-4 h-4" />
        </button>
      )}

      {expanded && (
        <div className="absolute bottom-2 right-2 font-osd text-[11px] text-[#d8b887] bg-[#1a0e05]/80 px-2 py-1 rounded border border-[#f5a623]/40 pointer-events-none">
          ROTATE &middot; ZOOM &middot; PAN
        </div>
      )}
    </div>
  );
}

function Badge({ children, testId }) {
  return (
    <span className="font-osd text-[11px] tracking-widest text-[#1a0e05] bg-[#9dff7a] px-2 py-0.5 rounded-sm shadow-[0_0_10px_rgba(140,255,110,0.55)]" data-testid={testId}>
      {children}
    </span>
  );
}
